import { ContentGroup } from '../../../../api/types/contentGroup';
import { Lesson } from '../../../../api/types/lesson';
import { MentorShort } from '../../../../api/types/mentorShort';

// ContentGroup.itemIds of a `collection` (course) are lesson IDs. Bundles hold
// other groups' IDs and are not counted as author courses.
export type AuthorCourseContent = {
  group: ContentGroup;
  lessons: Lesson[];
  qaLessons: Lesson[];
};

export type AuthorContent = {
  courses: AuthorCourseContent[];
  shorts: MentorShort[];
  coursesCount: number;
  lessonsCount: number;
  qaCount: number;
  shortsCount: number;
};

export type AuthorSetupFilter = 'complete' | 'missingCourse' | 'missingQa';

export const EMPTY_AUTHOR_CONTENT: AuthorContent = {
  courses: [],
  shorts: [],
  coursesCount: 0,
  lessonsCount: 0,
  qaCount: 0,
  shortsCount: 0,
};

export const buildAuthorContentMap = ({
  groups,
  lessons,
  shorts,
}: {
  groups: ContentGroup[];
  lessons: Lesson[];
  shorts: MentorShort[];
}): Record<string, AuthorContent> => {
  const lessonsById: Record<string, Lesson> = {};
  lessons.forEach((lesson) => {
    lessonsById[lesson.id] = lesson;
  });

  const map: Record<string, AuthorContent> = {};
  const getEntry = (authorId: string) => {
    if (!map[authorId]) {
      map[authorId] = { ...EMPTY_AUTHOR_CONTENT, courses: [], shorts: [] };
    }
    return map[authorId];
  };

  // Unique lesson IDs per author, so a lesson shared by two courses counts once.
  const lessonIdsByAuthor: Record<string, Set<string>> = {};
  const qaIdsByAuthor: Record<string, Set<string>> = {};

  groups.forEach((group) => {
    if (!group.authorId || group.type !== 'collection') {
      return;
    }
    const entry = getEntry(group.authorId);
    const groupLessons = (group.itemIds || []).map((id) => lessonsById[id]).filter((lesson) => !!lesson);
    const course: AuthorCourseContent = {
      group,
      lessons: groupLessons.filter((lesson) => !lesson.isQuestionAndAnswer),
      qaLessons: groupLessons.filter((lesson) => !!lesson.isQuestionAndAnswer),
    };
    entry.courses.push(course);
    lessonIdsByAuthor[group.authorId] = lessonIdsByAuthor[group.authorId] || new Set<string>();
    qaIdsByAuthor[group.authorId] = qaIdsByAuthor[group.authorId] || new Set<string>();
    course.lessons.forEach((lesson) => lessonIdsByAuthor[group.authorId as string].add(lesson.id));
    course.qaLessons.forEach((lesson) => qaIdsByAuthor[group.authorId as string].add(lesson.id));
  });

  shorts.forEach((short) => {
    if (short.authorId) {
      getEntry(short.authorId).shorts.push(short);
    }
  });

  Object.entries(map).forEach(([authorId, entry]) => {
    entry.shorts.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    entry.coursesCount = entry.courses.length;
    entry.lessonsCount = lessonIdsByAuthor[authorId]?.size || 0;
    entry.qaCount = qaIdsByAuthor[authorId]?.size || 0;
    entry.shortsCount = entry.shorts.length;
  });

  return map;
};

export const matchesSetupFilter = (content: AuthorContent | undefined, filter: AuthorSetupFilter): boolean => {
  const coursesCount = content?.coursesCount || 0;
  const qaCount = content?.qaCount || 0;
  switch (filter) {
    case 'missingCourse':
      return coursesCount === 0;
    case 'missingQa':
      return qaCount === 0;
    case 'complete':
      return coursesCount > 0 && qaCount > 0;
  }
};
